import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Select } from 'antd';
import {
  CheckOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  NumberOutlined
} from '@ant-design/icons';
import { floor } from '../../../../../../util/lodashlike';
import { normalizeAlertScore } from '../../../../../../util/normalization';
import style from './index.scss';
import CopyButton from '../CopyButton';
import { formatEventDateTime } from '../../../../../../util/timezone';
import { DDMMYYYYHHMMTimeFormat } from '../../../../../../util/GlobalConstant';

/**
 * FIN-11304 — the FRAUD PREVENTION header for the Spatial view.
 *
 *   ← Alerts   Alert Score ▓▓▓▓░ High 82.00/100   # Txn ID: f47d…  🕐 Timestamp: …
 *              Type: U9014   $ Source Amount: -   Source (USD): -   Destination Amount: -
 *                                                 [Txn ID: f47d…] [Txn Type: U9014]
 *
 * WHY A SECOND HEADER RATHER THAN A MODE ON AlertHeaderCard.
 * The two routes show genuinely different headers (user, from the designs):
 *   CM  — a CARD: Alert ID / Source / Date / Status / Assign to, plus the
 *         Variants-Breached / Action counters and the score, in AlertMetrics.
 *   FP  — a STRIP: the score leads, followed by inline transaction facts.
 * They share no structure — different container, different order, different
 * fields, and FP drops the counters entirely — so a flag on AlertHeaderCard would
 * have been two components in one file behind an if. Only the DATA is shared.
 *
 * NO NEW API, AND NO NEW CONTRACT. Every field here is already on
 * `amlsCommon.alertDetails`, the slice the shell loads for AlertHeaderCard, and
 * every one is already rendered by CLASSIC's own FP header
 * (SmartAssessmentTopPanel: ID / Timestamp / Type / Source Amount / Source (USD) /
 * Destination Amount / Alert Score). This is a re-layout of data already on
 * screen, which is why it needed nothing from the backend.
 *
 * DELIBERATELY NOT HERE, both per the design:
 *   • the Variants Breached / Action counters — CM keeps those (AlertMetrics)
 *   • Status / Note — an earlier FP design moved these to a floating bottom
 *     bar, which is separate work
 *
 * ALERT QUALITY IS NOW HERE, and the line above used to say it was not.
 * The design changed: the current prototype renders the quality select in this
 * header, and FIN-13562 was raised because Spatial had no
 * way to set it. The stale comment is why it read as deliberate at first —
 * corrected rather than deleted, so the history is legible.
 *
 * Classic additionally shows `Category` (e.g. "multi-account-same-device"); the
 * design omits it and the field has no obvious source on alertDetails, so it is
 * left out rather than guessed at. Flagged to the user.
 */

// A missing value renders a DASH (hyphen-minus, not an em dash — the design's
// own glyph), NOT a blank. The design itself shows
// "Source Amount: -" — on this surface a dash is the design's own way of saying
// "not sent", and an empty gap would read as a broken layout instead.
const DASH = '-';
const show = (value) =>
  value === null || value === undefined || value === '' ? DASH : value;

// FIN-14014 — amounts read as Classic's (SmartAssessmentTopPanel) do: "1000 SGD",
// so a non-USD amount is never mistaken for USD. The labels match Classic, except
// that the converted amount's label names txnCurrency rather than a fixed "USD"
// (Classic hardcodes it); USD or absent still reads "Source (USD)".
const showAmount = (amount, currency) =>
  show(amount) === DASH || !currency ? show(amount) : `${amount} ${currency}`;

// Level -> bar colour, matching AlertMetrics' LEVEL_TAG_COLOR so the same alert is
// badged identically whichever header renders it.
/* FALLBACK ONLY (FIN-14167) — the configured `alertLevelColor` is what paints
   the score now; this ladder is what is left when the alert carries no colour.

   `Good` IS IN THE MAP, and its absence was half the bug. The lookup falls back
   to `'red'`, so a level this map did not know took the MOST severe colour — and
   `Good` is precisely what the QA tenant calls its middle band (Low 80-89 /
   Good 90-99 / High 100). Every Good alert on that run instance therefore drew a
   red bar beside its own Good badge. An unknown level now lands on `grey`
   instead: a hue we were not told is better left unasserted than guessed at the
   top of the scale. */
const LEVEL_TONE = {
  High: { bar: 'red', figure: 'figureRed' },
  Good: { bar: 'orange', figure: 'figureOrange' },
  Medium: { bar: 'orange', figure: 'figureOrange' },
  Low: { bar: 'green', figure: 'figureGreen' }
};

/* Paired rather than two lookups: the bar and the figure sit side by side and a
   red number over a green bar states both ends of the scale at once — the shape
   of the defect this ticket is about. One entry keeps them in step. */
const UNKNOWN_TONE = { bar: 'grey', figure: 'figureGrey' };

const capitalise = (s) =>
  typeof s === 'string' && s.length
    ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    : s;

const Fact = ({ icon, label, value }) => (
  <span className={style.fact}>
    {icon || null}
    <span className={style.factLabel}>{label}</span>
    <span className={style.factValue}>{value}</span>
  </span>
);

Fact.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.node
};

/**
 * "05/11/2023 16:38" — the design's timestamp format.
 *
 * DD/MM/YYYY, matching EventTimeline's formatTimestamp, so the two surfaces on
 * this page cannot disagree about which number is the day. NO SECONDS: the design
 * shows none here, unlike the timeline's node panel.
 *
 * The BE sends this field in more than one shape — "2026-08-13 09:56:51" from the
 * unified_alerts columns, and full ISO with a Z from other paths. A bare space is
 * not valid ISO 8601 and Safari rejects it, so it is normalised to a T first.
 *
 * FALLS BACK TO THE RAW STRING when it cannot be parsed. Rendering "Invalid Date"
 * — or worse, an em dash — would hide a real value the analyst could otherwise
 * still read.
 */

// TRUNCATED IN JS, NOT CSS — matching the design, whose DOM carries two text
// nodes: "f47d2b13-8a3e-4f" and "…". Sixteen characters, then the ellipsis.
//
// The CSS `max-width: 180px` on .factValue cut at a PIXEL boundary instead, which
// let through about 27 characters at 11px mono — the same key rendered visibly
// longer than the design, and the cut moved with the font. A character count is
// what the design specifies and it is stable across platforms.
//
// The full value is never lost: it is the fact's hover title, and the copy
// button lifts it whole.
const KEY_VISIBLE_CHARS = 16;

const truncateKey = (value) =>
  typeof value === 'string' && value.length > KEY_VISIBLE_CHARS
    ? `${value.slice(0, KEY_VISIBLE_CHARS)}…`
    : value;

// SHAPE-GATED before touching Date, for two reasons found while testing this:
//   1. A blind `.replace(' ', 'T')` mangles anything that is not a timestamp —
//      "not a date" became "notTa date", which is neither the input nor a date.
//   2. `new Date(garbage)` returns Invalid Date in a browser, but the suite runs
//      with MockDate, which THROWS ("Unhandled date format passed to MockDate
//      constructor"). Relying on Invalid Date would pass in production and blow
//      up in tests — or the reverse, had the mock been laxer.
// Anything not matching is returned untouched.

const formatTxnTimestamp = (value) => {
  if (!value) return value;
  // FIN-13788 — this was `new Date(...)` plus browser-local getters and, unlike the
  // Transaction Information grid, applied no `time-adjustment`. The header therefore sat
  // exactly one adjustment away from the grid on the same alert: 11/07/2024 02:26 in the
  // header against 10/07/2024 20:56 in the grid and in the event's own Transaction
  // Timestamp. It is the first timestamp an analyst reads, and it was the wrong one.
  //
  // Now formatted through the shared event helper, so the header, the Spatial node, the
  // Spatial panel and the grid all read one value. The helper keeps the same shape guard
  // this function already had (DATE_SHAPE) — the suite runs under MockDate, which throws
  // rather than yielding Invalid Date.
  return formatEventDateTime(value, DDMMYYYYHHMMTimeFormat) || value;
};

// Classic's ids, labels and order verbatim (SmartAssessmentTopPanel:101-105).
// The order must match Classic: options are picked by position, so reversing
// them between views invites recording the wrong quality (FIN-13753).
export const ALERT_QUALITY_OPTIONS = [
  { value: 'high', label: 'High Quality' },
  { value: 'good', label: 'Good Quality' },
  { value: 'low', label: 'Low Quality' }
];

const FpAlertHeader = ({
  alertDetails,
  onBack,
  alertQuality,
  onAlertQualityChange,
  isSavingQuality,
  qualitySaveFailures
}) => {
  const d = alertDetails || {};

  /* THE CHOSEN VALUE HAS TO SURVIVE THE SAVE (FIN-13562, raised in review).
     `value` made this a CONTROLLED select reading `alertDetails.alertQuality`,
     and nothing updates that after a write: `amlsCommonAlertPartyUpdate` puts
     its success action and stops — no refetch — and the reducer does not touch
     the field. So the success toast appeared while the select snapped back to
     the previous value, or to "Please Select" on an alert that had none.
     Held locally until the store catches up, rather than switching to
     `defaultValue` as Classic does: uncontrolled would also ignore a value that
     arrives LATE, and `alertDetails` is fetched on mount, so on a slow response
     the select would be stuck empty for good.
     The local value yields as soon as the store agrees with it, so a refetch or
     a navigation is still authoritative. */
  const [pendingQuality, setPendingQuality] = useState(undefined);

  /* LOWERCASED, as Classic does (SmartAssessmentTopPanel:551). The options are
     keyed `low` / `good` / `high`; the BE has been seen to answer with other
     casing, which matches no option and renders the placeholder over a value the
     alert genuinely has. */
  const storedQuality =
    typeof alertQuality === 'string' ? alertQuality.toLowerCase() : undefined;

  useEffect(() => {
    if (pendingQuality && storedQuality === pendingQuality) {
      setPendingQuality(undefined);
    }
  }, [storedQuality, pendingQuality]);

  /* A REJECTED SAVE MUST NOT LEAVE THE CHOICE ON SCREEN. The shell increments
     this on every failure; dropping the local value returns the control to what
     the store actually holds, so the select stops claiming something the backend
     refused. A counter, not a boolean, so two consecutive failures are two
     events. */
  useEffect(() => {
    if (qualitySaveFailures) setPendingQuality(undefined);
  }, [qualitySaveFailures]);

  const selectedQuality = pendingQuality || storedQuality;

  /* FORWARDS EVERY ARGUMENT. antd calls onChange with (value, option) and the
     shell's handler is written against that pair; passing only the value
     silently dropped the second one. */
  const handleQualityChange = (value, ...rest) => {
    setPendingQuality(value);
    if (onAlertQualityChange) onAlertQualityChange(value, ...rest);
  };

  const {
    alertScore,
    alertLevel,
    alertLevelLabel,
    alertLevelColor,
    transactionKey,
    txnDateTime,
    transactionTypeCode,
    origTxnAmount,
    origCurrencyCode,
    txnAmount,
    txnCurrency,
    oppTxnAmount,
    oppCurrencyCode
  } = d;

  const numericScore = Number(alertScore);
  const hasScore = alertScore != null && Number.isFinite(numericScore);

  /**
   * THE CONFIGURED NAME, NOT THE CODE (FIN-13561).
   *
   * `alert_level_details` separates the DEFAULT code from the CURRENT
   * `display_name`, so a tenant who renames High to "Highfadsf" expects to see
   * that everywhere. This rendered `capitalise(alertLevel)` — the code — so
   * toggling one alert between the two views changed its severity label, and
   * Spatial showed a word that appears nowhere in the tenant's configuration.
   *
   * `alertLevelLabel` ARRIVES ON THE ALERT ITSELF, already resolved, which is
   * where Classic reads it from (SmartAssessmentTopPanel:513). Deliberately NOT
   * re-resolved here through `alertLevelConfigs` and `useAlertLevelConfigs`:
   * that is a second source for the same fact, and the two would disagree the
   * moment the fetch had not landed — the header would flash the code and then
   * correct itself. The capitalised code stays as the fallback, exactly as
   * Classic has it, for an alert whose label the BE did not resolve.
   */
  const level = alertLevelLabel || capitalise(alertLevel);

  /* THE COLOUR COMES OFF THE ALERT TOO (FIN-14167), for the same reason the
     LABEL above does: `alertLevelColor` is resolved by the BE from
     `alert_level_details` and arrives on the alert itself, so reading it here
     cannot disagree with the badge beside it or with Classic, which paints its
     Progress `strokeColor` from this very field (SmartAssessmentTopPanel:539).
     Re-resolving it through `useAlertLevelConfigs` would be the second source
     the comment above rules out.

     Inline, so it outranks the `.red/.orange/.green` classes without touching
     them — they stay as the fallback for an alert carrying no colour. Taken as
     given, with nothing appended, so no format guard is needed here: unlike
     AlertMetrics' tinted plate, both uses are a plain colour. */
  const scoreTint = alertLevelColor || undefined;
  const tone = LEVEL_TONE[level] || UNKNOWN_TONE;

  return (
    <div className={style.fpHeader}>
      <div className={style.topRow}>
        {/* A button, not an anchor: it goes back through the router rather than
            navigating, so an <a href> would be a lie to assistive tech and to
            middle-click. */}
        <button type="button" className={style.backLink} onClick={onBack}>
          {/* Arrow split from the word so the measured gap-1 (4px) actually
              applies — as one text node the separation would be whatever a
              space happens to be at 12px. */}
          <span aria-hidden="true">←</span>
          Alerts
        </button>

        {/* Hairline rule between the back link and the score, as the design
            draws it. Purely decorative, so it is hidden from assistive tech. */}
        <span className={style.divider} role="none" />

        {/* SCORE BLOCK — the design's leading element, and the reason the seeded
            alert needed ALERT_SCORE/ALERT_LEVEL populated: without them this is
            the most prominent thing on the page and it renders empty.
            Suppressed entirely when absent rather than showing a 0-width bar,
            which would assert a zero score. */}
        {hasScore ? (
          <span className={style.scoreBlock}>
            <span className={style.scoreLabel}>Alert Score</span>
            <span className={style.scoreRow}>
              <span className={style.scoreTrack}>
                <span
                  className={`${style.scoreFill} ${style[tone.bar]}`}
                  /* Clamped: a score outside 0-100 would otherwise paint a bar
                   wider than its own track. */
                  style={{
                    width: `${Math.max(0, Math.min(100, numericScore))}%`,
                    // Spread LAST, and only when configured, so an alert with no
                    // colour keeps the class ladder above rather than having its
                    // background explicitly unset.
                    ...(scoreTint ? { background: scoreTint } : {})
                  }}
                />
              </span>
              <span className={style.scoreValue}>
                {level ? (
                  <span className={style.scoreLevel}>{level}</span>
                ) : null}
                {/* CLASSIC'S OWN ARITHMETIC (FIN-13561), not `toFixed(2)`.
                    The design's "82.00" was read as a format instruction and was
                    not one: `toFixed` PADS, so a whole score rendered 100.00
                    here and 100 in Classic on the same alert. `floor(x, 2)`
                    truncates without padding and `normalizeAlertScore` clamps to
                    0..100, which is the pair SmartAssessmentTopPanel:529 uses —
                    shared so the two headers cannot drift again.
                    Wrapped so it can carry the destructive colour on its own —
                    the level beside it is foreground, not red. */}
                <span
                  className={`${style.scoreFigure} ${style[tone.figure]}`}
                  style={scoreTint ? { color: scoreTint } : undefined}>
                  {normalizeAlertScore(floor(numericScore, 2))}
                </span>
                <span className={style.scoreMax}>/100</span>
              </span>
            </span>
          </span>
        ) : null}

        {/* SECOND divider — the design puts one between the back link AND the
            score, and another between the score and the facts. Both are the same
            1px x 20px rule. */}
        <span className={style.divider} role="none" />

        <span className={style.facts}>
          <Fact
            icon={<NumberOutlined />}
            label="Txn ID:"
            value={
              <>
                {/* Full key on hover: the fact truncates it, and the chip that
                    used to show it whole is gone (FIN-13757). */}
                <span className={style.mono} title={transactionKey || undefined}>
                  {show(truncateKey(transactionKey))}
                </span>
                {/* Only when there is something to copy — a dash is not a value. */}
                {transactionKey ? (
                  <CopyButton
                    value={transactionKey}
                    ariaLabel="Copy transaction ID"
                  />
                ) : null}
              </>
            }
          />
          <Fact
            icon={<ClockCircleOutlined />}
            label="Timestamp:"
            value={show(formatTxnTimestamp(txnDateTime))}
          />
          <Fact label="Type:" value={show(transactionTypeCode)} />
          <Fact
            icon={<DollarOutlined />}
            label="Source Amount:"
            value={showAmount(origTxnAmount, origCurrencyCode)}
          />
          <Fact
            label={`Source (${txnCurrency || 'USD'}):`}
            value={showAmount(txnAmount, txnCurrency)}
          />
          <Fact
            label="Destination Amount:"
            value={showAmount(oppTxnAmount, oppCurrencyCode)}
          />
          {/* ALERT QUALITY — FIN-13562, the one editable control here. Same ids
              and same write (putAMLSCommonAlertPartyUpdate) as Classic.
              A labelled field IN the facts strip (FIN-13757): it used to sit on
              its own row beside chips that repeated Txn ID and Txn Type, which
              wrapped the header onto a third line and showed Txn ID twice. */}
          <span className={style.fact}>
            <span className={style.factLabel}>Alert Quality</span>
            <Select
              className={style.qualitySelect}
              dropdownClassName={style.qualityDropdown}
              value={selectedQuality || undefined}
              placeholder="Please Select"
              options={ALERT_QUALITY_OPTIONS}
              onChange={handleQualityChange}
              /* THE TICK IS OPT-IN. antd renders no
                 `.ant-select-item-option-state` for a single Select unless this
                 prop is passed, so the design's white check on the chosen row was
                 missing and the 32px tick gutter in the stylesheet was styling an
                 element that did not exist. Same prop ScenarioBar and the
                 Explanation filters pass, for the same reason. */
              menuItemSelectedIcon={<CheckOutlined />}
              /* Disabled while the write is in flight, as Classic does
               (setDisableSelectBox). Without it a slow save looks like nothing
               happened and invites a second click — the silent-no-op pattern
               FIN-13555 was raised for. */
              disabled={isSavingQuality || !onAlertQualityChange}
              loading={isSavingQuality}
              getPopupContainer={(node) => node.parentNode}
            />
          </span>
        </span>
      </div>
    </div>
  );
};

FpAlertHeader.propTypes = {
  // Raw amlsCommon.alertDetails.data — the same object AlertHeaderCard receives.
  alertDetails: PropTypes.object,
  onBack: PropTypes.func,
  // Current value, one of the ids above. Absent renders the placeholder.
  alertQuality: PropTypes.string,
  // Omitted on a surface with no write access — the select then renders
  // disabled rather than silently discarding the change.
  onAlertQualityChange: PropTypes.func,
  isSavingQuality: PropTypes.bool,
  // FIN-13562 — bumped by the shell whenever a save is rejected.
  qualitySaveFailures: PropTypes.number
};

export default FpAlertHeader;
